<template>
  <div
    class="modal webhook-upsert-modal"
    :class="{ 'is-active': isActive }"
    @keyup.esc="$emit('close-modal')"
  >
    <div class="modal-background"></div>
    <div class="modal-card">
      <header class="modal-card-head">
        <div>
          <p class="modal-card-title">{{ modalTitle }}</p>
        </div>
      </header>
      <section class="modal-card-body">
        <form
          id="webhook-request-form"
          class="is-centered"
          @submit.prevent="submitRequest"
        >
          <div class="form-fields">
            <div class="field columns">
              <FormLabel
                label-for="url"
                title="URL"
                :has-tooltip="true"
                tooltip-placement="tooltip-bottom"
                :tooltip-text="$t('webhooks.tooltips.url')"
              />
              <div class="control column is-9 is-flex">
                <div class="input-container">
                  <input
                    type="text"
                    id="url"
                    class="input is-shadowless"
                    autofocus
                    v-model="requestForm.url"
                    placeholder="Type here"
                    @change="validateRequestData('url')"
                  />
                  <p class="help is-danger" v-if="errors.url">
                    {{ errors.url }}
                  </p>
                </div>
                <div class="validation-container">
                  <i
                    class="material-icons"
                    v-if="validations.url && !errors.url"
                    >check_circle</i
                  >
                </div>
              </div>
            </div>

            <div class="field columns gray-bg">
              <FormLabel
                label-for="authorization-method"
                title="Authorization Method"
                :has-tooltip="true"
                :tooltip-text="$t('webhooks.tooltips.authorization_method')"
              />
              <div class="control column is-9">
                <div class="input-container">
                  <v-select
                    id="authorization-method"
                    label="label"
                    :options="authOptions"
                    :reduce="o => o.value"
                    :clearable='false'
                    :searchable='false'
                    v-model="requestForm.authMethod"
                    @input='$forceUpdate()'
                  ></v-select>
                </div>
              </div>
            </div>

            <div class="field columns gray-bg" v-if='requestForm.authMethod === "basic_auth"'>
              <FormLabel
                label-for="username"
                title="Username"
                :has-tooltip="true"
                :tooltip-text="$t('webhooks.tooltips.username')"
              />
              <div class="control column is-9 is-flex">
                <div class="input-container">
                  <input
                    type="text"
                    id="username"
                    class="input is-shadowless"
                    v-model="requestForm.basicAuth.username"
                    placeholder="Type here"
                    @change="validateRequestData('basicAuth.username')"
                  />
                  <p class="help is-danger" v-if="errors.basicAuth.username">
                    {{ errors.basicAuth.username }}
                  </p>
                </div>
                <div class="validation-container">
                  <i
                    class="material-icons"
                    v-if="validations.basicAuth.username && !errors.basicAuth.username"
                    >check_circle</i
                  >
                </div>
              </div>
            </div>

            <div class="field columns gray-bg" v-if='requestForm.authMethod === "basic_auth"'>
              <FormLabel
                label-for="password"
                :title="$t('register_form.password_label')"
                :has-tooltip="true"
                :tooltip-text="$t('webhooks.tooltips.password')"
              />
              <div class="control column is-9 is-flex">
                <div class="input-container">
                  <input
                    type="password"
                    id="password"
                    class="input is-shadowless"
                    v-model="requestForm.basicAuth.password"
                    placeholder="Type here"
                    @change="validateRequestData('basicAuth.password')"
                  />
                  <p class="help is-danger" v-if="errors.basicAuth.password">
                    {{ errors.basicAuth.password }}
                  </p>
                </div>
                <div class="validation-container">
                  <i
                    class="material-icons"
                    v-if="validations.basicAuth.password && !errors.basicAuth.password"
                    >check_circle</i
                  >
                </div>
              </div>
            </div>

            <div class="field columns gray-bg" v-if='requestForm.authMethod === "oauth2_client_credentials"'>
              <FormLabel
                label-for="oauth2_client_credentials_client_id"
                :title="$t('webhooks.form_labels.oauth2_client_credentials.client_id')"
                :has-tooltip="true"
                :tooltip-text="$t('webhooks.tooltips.oauth2_client_credentials.client_id')"
              />
              <div class="control column is-9 is-flex">
                <div class="input-container">
                  <input
                    type="text"
                    id="oauth2_client_credentials_client_id"
                    class="input is-shadowless"
                    v-model="requestForm.oauth2ClientCredentials.clientId"
                    placeholder="Type here"
                    @change="validateRequestData('oauth2ClientCredentials.clientId')"
                  />
                  <p class="help is-danger" v-if="errors.oauth2ClientCredentials.clientId">
                    {{ errors.oauth2ClientCredentials.clientId }}
                  </p>
                </div>
                <div class="validation-container">
                  <i
                    class="material-icons"
                    v-if="validations.oauth2ClientCredentials.clientId && !errors.oauth2ClientCredentials.clientId"
                  >check_circle</i>
                </div>
              </div>
            </div>

            <div class="field columns gray-bg" v-if='requestForm.authMethod === "oauth2_client_credentials"'>
              <FormLabel
                label-for="oauth2_client_credentials_client_secret"
                :title="$t('webhooks.form_labels.oauth2_client_credentials.client_secret')"
                :has-tooltip="true"
                :tooltip-text="$t('webhooks.tooltips.oauth2_client_credentials.client_secret')"
              />
              <div class="control column is-9 is-flex">
                <div class="input-container">
                  <input
                    type="password"
                    id="oauth2_client_credentials_client_secret"
                    class="input is-shadowless"
                    v-model="requestForm.oauth2ClientCredentials.clientSecret"
                    placeholder="Type here"
                    @change="validateRequestData('oauth2ClientCredentials.clientSecret')"
                  />
                  <p class="help is-danger" v-if="errors.oauth2ClientCredentials.clientSecret">
                    {{ errors.oauth2ClientCredentials.clientSecret }}
                  </p>
                </div>
                <div class="validation-container">
                  <i
                    class="material-icons"
                    v-if="validations.oauth2ClientCredentials.clientSecret && !errors.oauth2ClientCredentials.clientSecret"
                  >check_circle</i>
                </div>
              </div>
            </div>

            <div class="field columns gray-bg" v-if='requestForm.authMethod === "oauth2_client_credentials"'>
              <FormLabel
                label-for="oauth2_client_credentials_scope"
                :title="$t('webhooks.form_labels.oauth2_client_credentials.scope')"
                :has-tooltip="true"
                :tooltip-text="$t('webhooks.tooltips.oauth2_client_credentials.scope')"
              />
              <div class="control column is-9 is-flex">
                <div class="input-container">
                  <input
                    type="text"
                    id="oauth2_client_credentials_scope"
                    class="input is-shadowless"
                    v-model="requestForm.oauth2ClientCredentials.scope"
                    placeholder="Type here"
                    @change="validateRequestData('oauth2ClientCredentials.scope')"
                  />
                  <p class="help is-danger" v-if="errors.oauth2ClientCredentials.scope">
                    {{ errors.oauth2ClientCredentials.scope }}
                  </p>
                </div>
                <div class="validation-container">
                  <i
                    class="material-icons"
                    v-if="validations.oauth2ClientCredentials.scope && !errors.oauth2ClientCredentials.scope"
                  >check_circle</i>
                </div>
              </div>
            </div>

            <div class="field columns gray-bg" v-if='requestForm.authMethod === "oauth2_client_credentials"'>
              <FormLabel
                label-for="oauth2_client_credentials_authorization_endpoint"
                :title="$t('webhooks.form_labels.oauth2_client_credentials.authorization_endpoint')"
                :has-tooltip="true"
                :tooltip-text="$t('webhooks.tooltips.oauth2_client_credentials.authorization_endpoint')"
              />
              <div class="control column is-9 is-flex">
                <div class="input-container">
                  <input
                    type="text"
                    id="oauth2_client_credentials_authorization_endpoint"
                    class="input is-shadowless"
                    v-model="requestForm.oauth2ClientCredentials.authorizationEndpoint"
                    placeholder="Type here"
                    @change="validateRequestData('oauth2ClientCredentials.authorizationEndpoint')"
                  />
                  <p class="help is-danger" v-if="errors.oauth2ClientCredentials.authorizationEndpoint">
                    {{ errors.oauth2ClientCredentials.authorizationEndpoint }}
                  </p>
                </div>
                <div class="validation-container">
                  <i
                    class="material-icons"
                    v-if="validations.oauth2ClientCredentials.authorizationEndpoint && !errors.oauth2ClientCredentials.authorizationEndpoint"
                  >check_circle</i>
                </div>
              </div>
            </div>

            <div class="field">
              <div class="columns">
                <FormLabel
                  label-for="events"
                  title="Select events"
                  :has-tooltip="true"
                  :tooltip-text="$t('webhooks.tooltips.events')"
                />
              </div>
              <div class="columns">
                <div class="column is-3"></div>
                <div class="control column is-9">
                  <div class="event-options-container">
                    <div
                      v-for="(eventOption, index) in mergedEventOptions"
                      :key="index"
                      class="event-container"
                    >
                      <div class="check-box-container">
                        <p-check
                          class="p-icon p-curve p-smooth is-blue"
                          :class="{
                            'is-event-active':
                              requestForm.events[eventOption.name],
                          }"
                          v-model="requestForm.events[eventOption.name]"
                          @change="
                            validateRequestData(`events.${eventOption.name}`)
                          "
                          :disabled="disabledEvents[eventOption.name]"
                        >
                          <i class="icon material-icons" slot="extra"
                            >&#xE876;</i
                          >
                          <span class="event-option-label">{{
                            eventOption.label
                          }}</span>
                        </p-check>
                      </div>
                      <div class="tooltip-container">
                        <div class="is-relative">
                          <i class="material-icons">&#xE88F;</i>
                          <div class="tooltip tooltip-top" role="tooltip">
                            <div class="tooltip-outer">
                              <div class="tooltip-arrow"></div>
                              <div class="tooltip-inner">
                                {{
                                  $t(`webhooks.tooltips.${eventOption.name}`)
                                }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <p class="help is-danger clearfix" v-if="errors.events">
                    {{ errors.events }}
                  </p>
                </div>
              </div>
            </div>

            <div class="field columns">
              <FormLabel
                label-for="payload-size"
                title="Max payload size"
                :has-tooltip="true"
                :tooltip-text="$t('webhooks.tooltips.max_payload_size')"
              />
              <div class="control column is-9">
                <div class="input-container">
                  <v-select
                    id="payload-size"
                    label="label"
                    :options="batchPayloadSizeOptions"
                    :reduce="o => o.value"
                    v-model="requestForm.batchPayloadSize"
                    @input="validateRequestData('batchPayloadSize')"
                  ></v-select>
                </div>
                <p class="help is-danger" v-if="errors.batchPayloadSize">
                  {{ errors.batchPayloadSize }}
                </p>
              </div>
            </div>
            <div class="field columns">
              <FormLabel
                label-for="wait-time"
                title="Max wait time"
                :has-tooltip="true"
                :tooltip-text="$t('webhooks.tooltips.max_wait_time')"
              />
              <div class="control column is-9">
                <div class="input-container">
                  <v-select
                    id="wait-time"
                    label="label"
                    :options="batchMaxWaitTimeOptions"
                    v-model="requestForm.batchMaxWaitTime"
                    :reduce="o => o.value"
                    @input="validateRequestData('batchMaxWaitTime')"
                  ></v-select>
                </div>
                <p class="help is-danger" v-if="errors.batchMaxWaitTime">
                  {{ errors.batchMaxWaitTime }}
                </p>
              </div>
            </div>
            <div class="field columns">
              <FormLabel
                label-for="message-ttl"
                title="Message TTL"
                :has-tooltip="true"
                :tooltip-text="$t('webhooks.tooltips.message_ttl')"
              />
              <div class="control column is-9">
                <div class="input-container">
                  <v-select
                    id="message-ttl"
                    label="label"
                    :options="messageTtlOptions"
                    v-model="requestForm.messageTtl"
                    :reduce="o => o.value"
                    @input="validateRequestData('messageTtl')"
                  ></v-select>
                </div>
                <p class="help is-danger" v-if="errors.messageTtl">
                  {{ errors.messageTtl }}
                </p>
              </div>
            </div>
          </div>

          <div class="modal-card-footer is-flex">
            <button
              type="button"
              class="button cancel-btn"
              @click="$emit('close-modal')"
            >
              Cancel
            </button>
            <button class="button primary submit-request-btn">
              Submit Request
            </button>
          </div>
        </form>
      </section>
    </div>
  </div>
</template>

<script>
import Ajv from 'ajv';

import store from 'store';
import vSelect from 'vue-select';
import { mapState, mapGetters } from 'vuex';
import { pick, set, omit, get } from 'lodash';

import Api from 'services/Api';
import i18n from 'services/i18n';
import FormLabel from '../modules/FormLabel';
import notifications from 'services/Notifications';
import { isStatus2xx } from 'utils/http';
import { ERROR_CODES } from '../../constants'

const { keys, values } = Object;
const ajv = new Ajv({
  allErrors: true,
});
require('ajv-errors')(ajv);

const transportEvents = ['car_transports', 'all_transports'];

ajv.addKeyword('subscribedAtleastToOneEvent', {
  validate: function validate(data, dataObj) {
    if (!values(dataObj).some(val => val)) {
      // To add new errors push the error object to the function's errors property

      validate.errors = ajv.errors || [];

      if (
        !validate.errors.find(
          val =>
            val.keyword === 'subscribedAtleastToOneEvent' ||
            val.schemaPath.includes('subscribedAtleastToOneEvent')
        )
      ) {
        validate.errors.push({
          keyword: 'subscribedAtleastToOneEvent',
          message: i18n.t('webhooks.errors.subscribe_to_atleast_one_event'),
          params: {
            subscribedAtleastToOneEvent: false,
          },
        });
      }
      return false;
    }

    return true;
  },
});

ajv.addKeyword('subscribedToOnlyOneTransport', {
  validate: function validate(data, dataObj) {
    if (values(pick(dataObj, transportEvents)).filter(val => val).length > 1) {
      // To add new errors push the error object to the function's errors property
      validate.errors = ajv.errors || [];

      if (
        !validate.errors.find(
          val => val.keyword === 'subscribedToOnlyOneTransport'
        )
      ) {
        validate.errors.push({
          keyword: 'subscribedToOnlyOneTransport',
          message: i18n.t('webhooks.errors.not_more_than_one_transport'),
          params: {
            subscribedToOnlyOneTransport: false,
          },
        });
      }

      return false;
    }

    return true;
  },
});

export default {
  name: 'WebhookConfigUpsertModal',
  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    modalTitle: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapGetters('application', ['app']),
    ...mapState('webhook', ['webhookFormValidator', 'customMessageTypes']),
    ...mapState('webhook', {
      form: 'requestForm',
      errors: 'requestFormErrors',
    }),
    requestForm() {
      return { ...this.form, };
    },
    mergedEventOptions() {
      return [...this.eventOptions, ...this.customMessageTypes];
    },
    customTypesList() {
      return this.customMessageTypes.map(val => val.name);
    },
  },
  components: {
    vSelect,
    FormLabel,
  },
  data() {
    return {
      transportEvents,
      disabledEvents: {
        all_transports: false,
        car_transports: false,
        stationaries: false,
        predictions: false,
        locations: false,
        moments: false,
      },
      eventOptions: [
        {
          name: 'locations',
          label: 'Locations',
        },
        {
          name: 'predictions',
          label: 'Predictions',
        },
        {
          name: 'stationaries',
          label: 'Stationaries',
        },
        {
          name: 'moments',
          label: 'Moments',
        },
        {
          name: 'all_transports',
          label: 'All Transports',
        },
        {
          name: 'car_transports',
          label: 'Car Transports',
        },
      ],
      batchMaxWaitTimeOptions: [
        { label: '5 seconds', value: 5 },
        { label: '30 seconds', value: 30 },
        { label: '1 minute', value: 60 },
        { label: '5 minutes', value: 300 },
      ],
      messageTtlOptions: [
        { label: '4 hours', value: 14400 },
        { label: '1 day', value: 86400 },
        { label: '7 days', value: 604800 },
        { label: '28 days', value: 2419200 },
      ],
      batchPayloadSizeOptions: [
        { label: '32 KB', value: 32768 },
        { label: '128 KB', value: 131072 },
        { label: '1 MB', value: 1048576 },
        { label: '2 MB', value: 2097152 },
        { label: '4 MB', value: 4194304 },
      ],
      authOptions: [
        { label: 'Basic Auth', value: 'basic_auth' },
        { label: 'OAuth2 Client Credentials', value: 'oauth2_client_credentials' },
      ],
      validations: {
        url: false,
        basicAuth: {
          username: false,
          password: false,
        },
        oauth2ClientCredentials: {
          clientId: false,
          clientSecret: false,
          scope: false,
          authorizationEndpoint: false
        }
      },
    };
  },

  watch: {
    customMessageTypes(to, from) {
      to.forEach(val => (this.disabledEvents[val.name] = true));
    },
    form(to, from) {
      // Onload and on update, disable checkboxes when there is a change
      Object.keys(pick(to.events, this.transportEvents)).forEach(event => {
        if (to.events[event] !== from.events[event] || to.events[event]) {
          this.disableOtherTransportEvents(event);
        }
      });
    },
  },
  methods: {
    /**
     * Get's the relevant i8n error message that needs to show to the user
     * @param {String} errorCode
     * @return {String} i8n_message
     */
    getWebHookSubmitErrorMessage(errorCode) {
      switch(errorCode) {
        case ERROR_CODES.WEBHOOK_IP_ADDRESS_BLACKLISTED:
          return this.$t('webhooks.errors.ip_address_blacklisted');
        case ERROR_CODES.WEBHOOK_HTTP_NOT_ALLOWED:
          return this.$t('webhooks.errors.http_not_allowed');
        case ERROR_CODES.WEBHOOK_DNS_RESOLUTION_FAILURE:
          return this.$t('webhooks.errors.dns_resolution_failure');
        default:
          return this.$t('webhooks.errors.upsert_failed')
      }
    },

    setValidations(dataPaths = '', field) {
      if (dataPaths && dataPaths.includes(field)) {
        set(this.validations, field, false);
      } else {
        set(this.validations, field, true);
        set(this.errors, field, null);
      }
    },

    disableOtherTransportEvents(event) {
      if (this.transportEvents.includes(event)) {
        if (this.requestForm.events[event]) {
          // Transports allow only one to be active at a time. Disable other transport check boxes
          this.transportEvents.forEach(val => {
            if (!(val === event)) {
              this.disabledEvents[val] = true;
            }
          });
        } else {
          // When unchecked enable all tranport checkboxed
          this.transportEvents.forEach(val => {
            this.disabledEvents[val] = false;
          });
        }
      }
    },

    validateRequestData(field) {
      if (field && field.indexOf('events.') === 0) {
        const [actualField, event] = field.split('.');

        field = actualField;

        this.disableOtherTransportEvents(event);
      }

      // Trim the values that might have space
      this.requestForm.url = this.requestForm.url.trim();
      this.requestForm.basicAuth.username = this.requestForm.basicAuth.username.trim();
      ['clientId', 'clientSecret', 'scope', 'authorizationEndpoint'].forEach(val => {
        this.requestForm.oauth2ClientCredentials[val] = this.requestForm.oauth2ClientCredentials[val].trim();
      })

      ajv.validate(this.webhookFormValidator, this.requestForm);

      // Ajv includes the errors of url, username, password in the schemaPath of custom validations
      // which leads to weird scenarios later
      // {keyword: "errorMessage", dataPath: "/url", schemaPath: "#/properties/events/subscribedAtleastToOneEvent", params: {…}, message: "Entered url is invalid"}
      // The above is clear junk which should be filtered as dataPath and schema path does not match
      ajv.errors =
        ajv.errors && ajv.errors.filter(e => e.schemaPath.includes(e.keyword));

      const dataPaths =
        ajv.errors && ajv.errors.map(val => val.dataPath.replace(/\//g, '.')).join(' ');

      // Check and set the validations object
      if (field) {
        this.setValidations(dataPaths, field);
      } else {
        Object.keys(this.validations).forEach(val => {
          if (val === 'oauth2ClientCredentials') {
            Object.keys(this.validations.oauth2ClientCredentials).forEach(k => {
              this.setValidations(dataPaths, `oauth2ClientCredentials.${k}`);
            })
          } else if (val === 'basicAuth') {
            Object.keys(this.validations.basicAuth).forEach(k => {
              this.setValidations(dataPaths, `basicAuth.${k}`);
            })
          } else {
            this.setValidations(dataPaths, val);
          }
        });
      }

      // Check and set the validations error object
      ajv.errors &&
        ajv.errors.forEach(val => {
          // an example of val.dataPath: /oauth2ClientCredentials/clientSecret
          const splitDataPath = val.dataPath.split('/');
          // splitDataPath becomes ['', 'oauth2ClientCredentials', 'clientSecret']
          const key = splitDataPath.slice(1).join('.');
          // after slice and join, the key is 'oauth2ClientCredentials.clientSecret'
          // which is a valid path of requestFormErrors object where we can set an error and bind it to vue template
          if (field) {
            if (key === field) {
              set(this.errors, key, val.message);
            }
          } else {
            set(this.errors, key, val.message);
          }
        });
    },

    async submitRequest(event) {
      try {
        this.validateRequestData();

        const {
          url,
          authMethod,
          basicAuth: { username, password },
          oauth2ClientCredentials: { clientId, clientSecret, scope, authorizationEndpoint },
        } = this.requestForm
        const useBasicAuth = authMethod === 'basic_auth'

        if (values(omit(this.errors, ['basicAuth', 'oauth2ClientCredentials'])).some(v => v)) {
          return false;
        }

        if (useBasicAuth) {
          if (values(this.errors.basicAuth).some(v => v)) {
            return false;
          }
        } else {
          if (values(this.errors.oauth2ClientCredentials).some(v => v)) {
            return false;
          }
        }

        const params = {
          app_id: this.app.id,
          config: {
            url,
            ...(useBasicAuth? {
              auth: {
                username,
                password
              }
            }: {
              oauth2_client_credentials: {
                client_id: clientId,
                client_secret: clientSecret,
                scope,
                authorization_endpoint: authorizationEndpoint,
              }
            }),
            message_ttl: this.requestForm.messageTtl,
            batching: {
              max_wait_time: this.requestForm.batchMaxWaitTime,
              max_payload_size: this.requestForm.batchPayloadSize,
            },
            events: keys(this.requestForm.events).filter(
              val => this.requestForm.events[val]
            ),
          },
        };
        const response = await Api.ganderPortal.put(
          `apps/${params.app_id}/webhook_requests`,
          params
        );

        if (isStatus2xx(response.status)) {
          // Update the store with new request data and the start polling for status
          store.commit('webhook/setRequestedWebhook', response.body);
          this.$emit('close-modal');
          this.$emit('request-success');
        }
      } catch (error) {
        // Display the relevant error message
        notifications.add(
          this.getWebHookSubmitErrorMessage(get(error, 'body.code', ''))
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';
</style>

<style lang="scss">
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';

.pretty .state label {
  color: #6b6767;
}
.pretty .state label:before {
  border-color: $grey-light;
}
.pretty input[disabled] ~ * {
  opacity: 0.25;
}
.is-event-active.pretty .state label {
  color: $black;
}
.is-relative {
  position: relative;
}
</style>
